import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Avatar,
  Stack,
  IconButton,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { format, parseISO } from 'date-fns';
import dayjs, { Dayjs } from 'dayjs';

import ShiftTimePicker from '@/components/ShiftTimePicker';
import { GetJobQuery } from '@/types/graphql';

interface CheckoutModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (checkoutTime: string, breakMinutes: number) => void;
  workerInfo: {
    id: string;
    firstName: string;
    lastName: string;
    rating?: number;
    reviewCount?: number;
    avatar?: string;
  };
  currentCheckoutTime?: Date | null;
  currentBreakMinutes?: number | null;
  timezone: string;
}

const CheckoutModal: React.FC<CheckoutModalProps> = ({
  open,
  onClose,
  onConfirm,
  workerInfo,
  currentCheckoutTime,
  currentBreakMinutes,
  timezone,
}) => {
  const [selectedTime, setSelectedTime] = useState<Dayjs | null>(null);
  const [breakMinutes, setBreakMinutes] = useState<number>(0);
  const [selectContainer, setSelectContainer] = useState<HTMLElement | null>(
    null,
  );

  useEffect(() => {
    if (open) {
      // Set default time to current time or existing checkout time
      const defaultTime = currentCheckoutTime
        ? dayjs(currentCheckoutTime)
        : dayjs();
      setSelectedTime(defaultTime);
      setBreakMinutes(currentBreakMinutes || 0);
      // Set container to document.body to render outside modal
      setSelectContainer(document.body);
    }
  }, [open, currentCheckoutTime, currentBreakMinutes]);

  const handleTimeChange = (time: Dayjs | null) => {
    setSelectedTime(time);
  };

  const handleBreakChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setBreakMinutes(Number(event.target.value));
  };

  const handleConfirm = () => {
    if (selectedTime) {
      const timeString = selectedTime.format('HH:mm');
      onConfirm(timeString, breakMinutes);
    }
  };

  const handleClose = () => {
    setSelectedTime(null);
    setBreakMinutes(0);
    onClose();
  };

  const getWorkerName = () => {
    console.log('CheckoutModal workerInfo:', workerInfo);
    return `${workerInfo.firstName} ${workerInfo.lastName}`;
  };

  const getRatingDisplay = () => {
    if (workerInfo.rating && workerInfo.reviewCount) {
      return `${workerInfo.rating} (${workerInfo.reviewCount})`;
    }
    return null;
  };

  // Generate break minute options (0-90 minutes in 15-minute increments)
  const breakOptions = Array.from({ length: 7 }, (_, i) => i * 15);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '8px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
          overflow: 'visible',
        },
      }}
      sx={{
        '& .MuiDialog-paper': {
          overflow: 'visible',
        },
        '& .MuiDialogContent-root': {
          overflow: 'visible',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '24px 24px 8px 24px',
          borderBottom: 'none',
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            fontSize: '18px',
            color: '#333333',
          }}
        >
          Check-out
        </Typography>
        <IconButton
          onClick={handleClose}
          sx={{
            padding: '4px',
            color: '#666666',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          padding: '8px 24px 16px 24px',
          overflow: 'visible',
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: '#666666',
            marginBottom: '20px',
            fontSize: '14px',
          }}
        >
          Set and confirm this worker's check-out time and break.
        </Typography>

        {/* Worker Profile Section */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            marginBottom: '24px',
            padding: '12px',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
          }}
        >
          <Box sx={{ position: 'relative', marginRight: '16px' }}>
            <Avatar
              src={workerInfo.avatar}
              sx={{
                width: 64,
                height: 64,
                backgroundColor: '#e0e0e0',
                fontSize: '20px',
                fontWeight: 600,
                color: '#333333',
              }}
            >
              {workerInfo.firstName?.[0]}
              {workerInfo.lastName?.[0]}
            </Avatar>
            <Box
              sx={{
                position: 'absolute',
                bottom: -2,
                right: -2,
                width: 20,
                height: 20,
                backgroundColor: '#45A735',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '2px solid white',
              }}
            >
              <Typography
                sx={{
                  color: 'white',
                  fontSize: '12px',
                  fontWeight: 700,
                }}
              >
                ✓
              </Typography>
            </Box>
          </Box>

          <Box>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: '18px',
                color: '#333333',
                marginBottom: '4px',
              }}
            >
              {getWorkerName()}
            </Typography>
            {getRatingDisplay() && (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography
                  sx={{
                    fontSize: '14px',
                    color: '#FFA500',
                    marginRight: '4px',
                  }}
                >
                  ★
                </Typography>
                <Typography
                  sx={{
                    fontSize: '14px',
                    color: '#666666',
                  }}
                >
                  {getRatingDisplay()}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        {/* Input Fields Section */}
        <Stack spacing={3}>
          {/* Check-out Time Field */}
          <Box>
            <Typography
              sx={{
                fontSize: '14px',
                fontWeight: 500,
                color: '#333333',
                marginBottom: '8px',
              }}
            >
              Check-out
            </Typography>
            <ShiftTimePicker
              value={selectedTime}
              onChange={handleTimeChange}
              inputStyle={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '6px',
                  border: '1px solid #e0e0e0',
                  '&:hover': {
                    borderColor: '#45A735',
                  },
                  '&.Mui-focused': {
                    borderColor: '#45A735',
                    boxShadow: '0 0 0 2px rgba(69, 167, 53, 0.2)',
                  },
                },
                '& .MuiInputBase-input': {
                  padding: '12px 14px',
                  fontSize: '16px',
                  fontWeight: 600,
                  color: '#333333',
                },
              }}
              timeSteps={{ hours: 1, minutes: 1, seconds: 1 }}
              format="hh:mm a"
              ampm={true}
              PopperProps={{
                style: {
                  zIndex: 9999,
                },
                placement: 'bottom-start',
                modifiers: [
                  {
                    name: 'preventOverflow',
                    enabled: true,
                    options: {
                      boundary: 'viewport',
                    },
                  },
                  {
                    name: 'flip',
                    enabled: true,
                  },
                ],
              }}
            />
          </Box>

          {/* Break Minutes Field */}
          <Box>
            <Typography
              sx={{
                fontSize: '14px',
                fontWeight: 500,
                color: '#333333',
                marginBottom: '8px',
              }}
            >
              Break
            </Typography>
            <FormControl fullWidth>
              <Select
                value={breakMinutes}
                onChange={(e) => setBreakMinutes(Number(e.target.value))}
                displayEmpty
                MenuProps={{
                  container: selectContainer,
                  style: {
                    zIndex: 99999,
                  },
                  PaperProps: {
                    style: {
                      maxHeight: 200,
                      zIndex: 99999,
                    },
                  },
                  anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'left',
                  },
                  transformOrigin: {
                    vertical: 'top',
                    horizontal: 'left',
                  },
                }}
                sx={{
                  borderRadius: '6px',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#e0e0e0',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#45A735',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#45A735',
                    boxShadow: '0 0 0 2px rgba(69, 167, 53, 0.2)',
                  },
                  '& .MuiSelect-select': {
                    padding: '12px 14px',
                    fontSize: '16px',
                    fontWeight: 600,
                    color: '#333333',
                  },
                }}
              >
                {breakOptions.map((minutes) => (
                  <MenuItem key={minutes} value={minutes}>
                    {minutes} min
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions
        sx={{
          padding: '16px 24px 24px 24px',
          gap: '12px',
          justifyContent: 'flex-end',
        }}
      >
        <Button
          onClick={handleClose}
          variant="outlined"
          sx={{
            borderRadius: '6px',
            borderColor: '#45A735',
            color: '#45A735',
            fontWeight: 600,
            textTransform: 'none',
            padding: '10px 20px',
            '&:hover': {
              borderColor: '#45A735',
              backgroundColor: 'rgba(69, 167, 53, 0.04)',
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={!selectedTime}
          sx={{
            borderRadius: '6px',
            backgroundColor: '#45A735',
            color: 'white',
            fontWeight: 600,
            textTransform: 'none',
            padding: '10px 20px',
            '&:hover': {
              backgroundColor: '#3a8f2e',
            },
            '&:disabled': {
              backgroundColor: '#e0e0e0',
              color: '#999999',
            },
          }}
        >
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CheckoutModal;
